import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { jobsApi, proposalsApi } from '../services/api';
import { CategoryIcon } from '../components/ui/CategoryIcon';
import { MaterialIcon } from '../components/ui/MaterialIcon';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';

// Calca ScreenPostular de screens-job.jsx (Claude Design): un sheet único, no un wizard.
export const SubmitProposal: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<any>(null);
  const [amount, setAmount] = useState('');
  const [estimatedDays, setEstimatedDays] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    jobsApi.get(Number(id)).then(({ data }) => setJob(data.job));
  }, [id]);

  const close = () => navigate(-1);

  const handleSubmit = async () => {
    setError('');
    if (!amount || Number(amount) <= 0) {
      setError('Ingresá un monto válido.');
      return;
    }
    if (!message.trim()) {
      setError('Escribí un mensaje para el cliente.');
      return;
    }
    setSubmitting(true);
    try {
      await proposalsApi.create(Number(id), {
        cover_letter: message,
        amount: Number(amount),
        estimated_days: estimatedDays ? Number(estimatedDays) : null,
      });
      navigate(`/jobs/${id}`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'No pudimos enviar tu propuesta.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50" onClick={close}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-surface rounded-t-[28px] max-h-[92vh] flex flex-col shadow-2xl"
      >
        <div className="flex justify-center pt-2.5">
          <div className="w-9 h-1 rounded-full bg-outlineVarWarm" />
        </div>
        <div className="flex items-center justify-between px-6 pt-3 pb-2">
          <h2 className="text-xl font-bold text-onSurface">Tu propuesta</h2>
          <button onClick={close} className="text-onSurface-mute hover:text-onSurface p-1">
            <MaterialIcon name="close" size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6">
          {job && (
            <div className="flex gap-2.5 p-2.5 bg-cream2 rounded-xl mb-6">
              <span className="w-9 h-9 rounded-xl bg-surface flex items-center justify-center shrink-0">
                <CategoryIcon name={job.category?.icon} size={20} className="text-primary-700" />
              </span>
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-onSurface truncate">{job.title}</p>
                <p className="text-[11px] text-onSurface-mute">Presupuesto pretendido: {job.budget_range}</p>
              </div>
            </div>
          )}

          {error && <div className="mb-4 p-3 rounded-xl bg-danger-50 text-danger-700 text-sm">{error}</div>}

          <div className="flex flex-col gap-6 pb-2">
            <Input
              pill
              label="Tu precio (ARS)"
              type="number"
              min={1}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              helper="El cliente verá este monto"
              required
            />
            <Input
              pill
              label="Días estimados (opcional)"
              type="number"
              min={1}
              value={estimatedDays}
              onChange={(e) => setEstimatedDays(e.target.value)}
              placeholder="Ej: 3"
            />
            <div>
              <label className="text-xs font-bold text-onSurface-var uppercase tracking-wide mb-2 block">
                Mensaje para el cliente
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, 500))}
                rows={5}
                maxLength={500}
                placeholder="Contá tu experiencia y cómo encararías el trabajo..."
                className="w-full rounded-xl border border-outlineWarm bg-surface p-3.5 text-sm text-onSurface leading-relaxed outline-none focus:border-primary-500 resize-none"
              />
              <p className="text-xs text-onSurface-mute mt-1 text-right">{message.length}/500</p>
            </div>
          </div>
        </div>

        <div className="px-6 pt-3 pb-[calc(1.75rem+env(safe-area-inset-bottom))] border-t border-outlineVarWarm">
          <Button variant="filled" pill size="xl" className="w-full justify-center" onClick={handleSubmit} isLoading={submitting}>
            <MaterialIcon name="send" size={18} /> Enviar propuesta
          </Button>
        </div>
      </div>
    </div>
  );
};
